import { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface ParentOption { value: string; label: string; }

interface Props {
  value: string;
  options: ParentOption[];
  onChange: (v: string) => void;
  theme?: "light" | "dark";
  disabled?: boolean;
  /** Control (and anchored-popup) width. */
  width?: number | string;
  maxWidth?: number | string;
  placeholder?: string;
  /** When true, the option list opens as a centered screen overlay instead of anchored below the control. */
  centered?: boolean;
  /** Header shown atop the centered overlay. */
  title?: string;
}

/**
 * A custom Parent Page dropdown. Replaces the native <select> so we can:
 *  - avoid the Chromium native-popup "missing white strip until you scroll" glitch,
 *  - style the option list (orange hover matching the sidebar), and
 *  - control the popup width.
 */
export default function ParentSelect({
  value, options, onChange, theme = "light", disabled = false,
  width = "100%", maxWidth, placeholder = "— Select —", centered = false, title = "Select Parent Page",
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const lt = theme === "light";

  const selected = options.find(o => o.value === value);

  // Close on outside click / Escape. (Centered mode uses its own backdrop click; only Escape here.)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!centered && rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, centered]);

  // Scroll the selected row into view when opening.
  useLayoutEffect(() => {
    if (open && menuRef.current) {
      const active = menuRef.current.querySelector<HTMLElement>(".psel-opt.psel-active");
      if (active) active.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  const bg = lt ? "#ffffff" : "#1e293b";
  const fg = lt ? "#0f172a" : "#e2e8f0";
  const bdr = lt ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.15)";
  const btnBg = lt ? "#ffffff" : "rgba(255,255,255,.06)";

  const btn: CSSProperties = {
    width: "100%", boxSizing: "border-box", padding: "9px 11px", background: btnBg, border: bdr,
    borderRadius: 6, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: selected ? fg : (lt ? "#94a3b8" : "rgba(255,255,255,.5)"),
    outline: "none", cursor: disabled ? "not-allowed" : "pointer", textAlign: "left",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  };

  const renderOptions = (truncate: boolean) => options.map(o => {
    const active = o.value === value;
    return (
      <div
        key={o.value}
        role="option"
        aria-selected={active}
        className={"psel-opt" + (active ? " psel-active" : "")}
        style={{
          padding: "8px 10px", borderRadius: 5, fontSize: 13, color: fg, cursor: "pointer",
          whiteSpace: "nowrap", fontWeight: active ? 700 : 400,
          ...(truncate ? { overflow: "hidden", textOverflow: "ellipsis" } : { width: "max-content", minWidth: "100%", boxSizing: "border-box" }),
        }}
        onClick={() => { onChange(o.value); setOpen(false); }}
      >{o.label}</div>
    );
  });

  return (
    <div ref={rootRef} className="psel" style={{ position: "relative", width, maxWidth }}>
      <button type="button" style={btn} disabled={disabled} onClick={() => !disabled && setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected ? selected.label : placeholder}</span>
        <span style={{ flexShrink: 0, opacity: .6, fontSize: 10, transform: open ? "rotate(180deg)" : "none", transition: "transform .12s" }}>▼</span>
      </button>

      {/* Anchored dropdown (default) */}
      {open && !centered && (
        <div
          ref={menuRef}
          className="psel-menu"
          role="listbox"
          style={{
            // Grow to fit the widest URL (min = control width) so options aren't cut off;
            // cap to the viewport and scroll if a URL is longer than the cap.
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            width: "max-content", minWidth: "100%", maxWidth: "min(760px, 92vw)",
            maxHeight: 300, overflow: "auto", background: bg, border: bdr, borderRadius: 8,
            boxShadow: "0 12px 32px rgba(15,23,42,.28)", zIndex: 60, padding: 4,
          }}
        >
          {renderOptions(false)}
        </div>
      )}

      {/* Centered screen overlay */}
      {open && centered && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onMouseDown={() => setOpen(false)}
        >
          <div
            className="psel-menu"
            role="listbox"
            style={{
              width: "min(880px, 92vw)", maxWidth: "100%", maxHeight: "72vh", display: "flex", flexDirection: "column",
              background: bg, border: bdr, borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,.45)",
              fontFamily: "'Inter','Mulish',sans-serif", overflow: "hidden",
            }}
            onMouseDown={e => e.stopPropagation()}
          >
            <div style={{ padding: "14px 16px", borderBottom: bdr, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: fg }}>{title}</span>
              <button type="button" onClick={() => setOpen(false)} style={{ border: "none", background: "none", color: lt ? "#64748b" : "rgba(255,255,255,.6)", fontSize: 20, lineHeight: 1, cursor: "pointer", padding: 0 }}>×</button>
            </div>
            <div ref={menuRef} style={{ overflow: "auto", padding: 8 }}>
              {renderOptions(false)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
